import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronRight, MapPin, Clock, Building2, Briefcase, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api";
import Reveal from "@/components/Reveal";
import NotFound from "@/pages/NotFound";

const inputCls =
  "w-full rounded-lg border border-navy-900/15 bg-white px-4 py-2.5 text-sm text-navy-900 placeholder:text-slate-400 focus:border-gold-dark focus:outline-none focus:ring-2 focus:ring-gold/30";

export default function JobDetail() {
  const { slug } = useParams();
  const [job, setJob] = useState(null);
  const [missing, setMissing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    fullName: "", email: "", phone: "", location: "", linkedin: "", portfolio: "",
    experience: "", currentCompany: "", noticePeriod: "", expectedCompensation: "", coverNote: "",
  });
  const [resume, setResume] = useState(null);

  useEffect(() => {
    setJob(null);
    setMissing(false);
    api.get(`/jobs/by-slug/${slug}`)
      .then((r) => setJob(r.data))
      .catch(() => setMissing(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (missing) return <NotFound />;
  if (!job) return <div className="min-h-screen bg-navy-950" data-testid="job-loading" />;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.fullName.trim() || !form.email.trim()) {
      toast.error("Please provide your full name and email.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("position", job.title);
      fd.append("brand", job.brand || "");
      fd.append("jobId", job.id);
      if (resume) fd.append("resume", resume);
      await api.post("/applications", fd);
      setSubmitted(true);
      toast.success("Application submitted successfully.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit your application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid={`job-page-${slug}`}>
      <section className="bg-navy-950 pb-20 pt-40 lg:pt-48">
        <div className="container-rcs">
          <Reveal>
            <nav aria-label="Breadcrumb" className="mb-8 flex items-center gap-2 text-xs text-slate-400" data-testid="breadcrumb">
              <Link to="/" className="transition-colors hover:text-gold">Home</Link>
              <ChevronRight className="h-3 w-3" aria-hidden />
              <Link to="/careers" className="transition-colors hover:text-gold">Careers</Link>
              <ChevronRight className="h-3 w-3" aria-hidden />
              <span className="text-gold">{job.title}</span>
            </nav>
            <h1 className="font-serif text-4xl text-ivory sm:text-5xl">{job.title}</h1>
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-300">
              {job.department && <span className="inline-flex items-center gap-2"><Briefcase className="h-4 w-4 text-gold" aria-hidden />{job.department}</span>}
              {job.brand && <span className="inline-flex items-center gap-2"><Building2 className="h-4 w-4 text-gold" aria-hidden />{job.brand}</span>}
              {job.location && <span className="inline-flex items-center gap-2"><MapPin className="h-4 w-4 text-gold" aria-hidden />{job.location}{job.workMode ? ` · ${job.workMode}` : ""}</span>}
              {job.employmentType && <span className="inline-flex items-center gap-2"><Clock className="h-4 w-4 text-gold" aria-hidden />{job.employmentType}</span>}
              {job.experience && <span className="text-slate-400">Experience: {job.experience}</span>}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28" data-testid="job-body">
        <div className="container-rcs grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <h2 className="eyebrow">About the Role</h2>
            <p className="mt-5 whitespace-pre-line text-base leading-relaxed text-slate-600" data-testid="job-description">
              {job.description || "Role details will be shared with shortlisted candidates."}
            </p>
          </div>

          <Reveal delay={0.15} className="lg:col-span-6">
            <div className="rounded-2xl border border-navy-900/10 bg-white p-8 lg:p-10" data-testid="application-form-card">
              {submitted ? (
                <div className="py-10 text-center" data-testid="application-success">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-navy-950 text-gold">
                    <UploadCloud className="h-6 w-6" aria-hidden />
                  </div>
                  <h3 className="mt-6 font-serif text-2xl text-navy-900">Application received.</h3>
                  <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-slate-600">
                    Thank you for your interest in RCS. Our team will review your application and reach out if there is a fit.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="application-form" className="space-y-4">
                  <h2 className="font-serif text-2xl text-navy-900">Apply for this role</h2>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <input required aria-label="Full name" placeholder="Full Name *" className={inputCls} value={form.fullName} onChange={set("fullName")} data-testid="app-full-name" />
                    <input required type="email" aria-label="Email" placeholder="Email *" className={inputCls} value={form.email} onChange={set("email")} data-testid="app-email" />
                    <input aria-label="Phone" placeholder="Phone" className={inputCls} value={form.phone} onChange={set("phone")} data-testid="app-phone" />
                    <input aria-label="Location" placeholder="Location" className={inputCls} value={form.location} onChange={set("location")} data-testid="app-location" />
                    <input aria-label="LinkedIn" placeholder="LinkedIn URL" className={inputCls} value={form.linkedin} onChange={set("linkedin")} data-testid="app-linkedin" />
                    <input aria-label="Portfolio" placeholder="Portfolio / Website" className={inputCls} value={form.portfolio} onChange={set("portfolio")} data-testid="app-portfolio" />
                    <input aria-label="Experience" placeholder="Years of Experience" className={inputCls} value={form.experience} onChange={set("experience")} data-testid="app-experience" />
                    <input aria-label="Current company" placeholder="Current Company" className={inputCls} value={form.currentCompany} onChange={set("currentCompany")} data-testid="app-current-company" />
                    <input aria-label="Notice period" placeholder="Notice Period" className={inputCls} value={form.noticePeriod} onChange={set("noticePeriod")} data-testid="app-notice-period" />
                    <input aria-label="Expected compensation" placeholder="Expected Compensation" className={inputCls} value={form.expectedCompensation} onChange={set("expectedCompensation")} data-testid="app-compensation" />
                  </div>
                  <textarea aria-label="Cover note" placeholder="Cover Note" rows={4} className={inputCls} value={form.coverNote} onChange={set("coverNote")} data-testid="app-cover-note" />
                  <div>
                    <label htmlFor="resume-upload" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Resume (PDF / DOC / DOCX, max 10 MB)
                    </label>
                    <input
                      id="resume-upload"
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={(e) => setResume(e.target.files?.[0] || null)}
                      className="w-full text-sm text-slate-600 file:mr-4 file:rounded-full file:border-0 file:bg-navy-950 file:px-5 file:py-2.5 file:text-xs file:font-semibold file:text-ivory hover:file:bg-navy-800"
                      data-testid="app-resume"
                    />
                  </div>
                  <button type="submit" disabled={submitting} className="btn-gold w-full justify-center disabled:opacity-60" data-testid="app-submit">
                    {submitting ? "Submitting…" : "Submit Application"}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
